import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import logoWhite from '../../../shared/assets/logo/liftpeak_white.png';
import '../../privacy/ui/LegalPage.css';

export function Terms() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="legal-page">
      <header className="legal-header">
        <Link to="/" className="legal-logo-link">
          <img src={logoWhite} className="legal-logo" alt="Liftpeak logo" />
        </Link>
      </header>

      <main className="legal-content">
        <h1 className="legal-title">Terms of Use</h1>
        <p className="legal-updated">Last updated: 2026-06-15</p>

        <p>
          These Terms and Conditions ("Terms") govern your use of the LiftPeak mobile application
          and our website (the "Service"). By creating an account or using the Service, you agree
          to these Terms.
        </p>

        <section className="legal-section">
          <h2>1) Owner / Company</h2>
          <p>The Service is operated by <strong>LiftPeak OU</strong>, Harju maakond, Tallinn, Lasnamae linnaosa, Sepapaja tn 6, 15551, Estonia ("LiftPeak", "we", "us").</p>
          <p><strong>Contact:</strong> <a href="mailto:contact@liftpeak.com">contact@liftpeak.com</a></p>
        </section>

        <section className="legal-section">
          <h2>2) Eligibility</h2>
          <p>The Service is intended for users <strong>18 years or older</strong>. By using the Service, you confirm that you are at least 18. If we learn that a user is under 18, we may suspend or delete the account.</p>
        </section>

        <section className="legal-section">
          <h2>3) Accounts & security</h2>
          <ul>
            <li>You must provide accurate account information and keep it up to date.</li>
            <li>You are responsible for activity on your account and for keeping your login credentials secure.</li>
            <li>You must not share your account or use someone else's account without permission.</li>
            <li>We may suspend or terminate accounts that violate these Terms or pose a security risk.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>4) The Service</h2>
          <p>LiftPeak provides tools to log workouts, track progress, and (optionally) share content and interact with other users. We may update or change features at any time, and some features may be added, removed, or modified.</p>
        </section>

        <section className="legal-section">
  <h2>5) Community rules (user conduct)</h2>

  <p>
    <strong>
      LiftPeak has zero tolerance for objectionable content or abusive behavior.
      Content that violates these rules may be removed immediately, and users who
      engage in abusive behavior may be suspended or permanently banned.
    </strong>
  </p>

  <p>You agree not to use the Service to:</p>
  <ul>
    <li>post or share unlawful, harmful, threatening, abusive, harassing, hateful, or defamatory content;</li>
    <li>post sexually explicit content or exploitative content;</li>
    <li>infringe others&apos; rights (copyright, trademark, privacy, publicity);</li>
    <li>impersonate others or misrepresent your identity;</li>
    <li>attempt to hack, disrupt, scrape, reverse engineer, or bypass security measures;</li>
    <li>upload malware or misuse the Service in a way that harms other users or the Service.</li>
  </ul>

  <p>
    We may remove content, limit visibility, or restrict accounts if we believe
    content or behavior violates these Terms or creates risk.
  </p>
</section>


        <section className="legal-section">
          <h2>6) User content & license</h2>
          <p>The Service may allow you to upload or post content (including workouts, text, images, comments, and profile information) ("User Content").</p>
          <ul>
            <li><strong>You own your User Content</strong>, but you grant LiftPeak a <strong>worldwide, non-exclusive, royalty-free license</strong> to host, store, reproduce, modify (for formatting), display, and distribute your User Content only to operate, provide, and improve the Service (including showing your content according to your privacy settings).</li>
            <li>You confirm you have the rights to upload your User Content and that it does not violate law or others' rights.</li>
          </ul>
          <p><strong>Content removal:</strong> You can delete content in the app (where available). Deletion may not be immediate everywhere (e.g., backups), and some public interactions may be anonymized to preserve conversation threads.</p>
        </section>

        <section className="legal-section">
          <h2>7) Reporting, moderation, and enforcement</h2>
          <p>Users can report content or behavior. We may review reports and take action we consider appropriate, including removing content or suspending accounts. We are not obligated to monitor all content, and we do not guarantee removal of any specific content.</p>
        </section>

        <section className="legal-section">
          <h2>8) Health & fitness disclaimer</h2>
          <p>LiftPeak is provided for informational and tracking purposes only and is <strong>not medical advice</strong>.</p>
          <ul>
            <li>Consult a qualified professional before starting any training program.</li>
            <li>Training carries risk of injury. You assume all risks related to workouts you perform.</li>
            <li>LiftPeak is not responsible for injuries or health issues arising from your training or from workouts shared by other users.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>9) Third-party services (Apple/Google, analytics, etc.)</h2>
          <p>Your use of the Service may involve third-party platforms and providers (including Apple, Google, RevenueCat, and analytics providers). Their terms and privacy policies may apply. LiftPeak is not responsible for third-party services outside our control.</p>
        </section>

        <section className="legal-section">
          <h2>10) Intellectual property</h2>
          <p>LiftPeak and its software, branding, and design are owned by LiftPeak or its licensors. You may not copy, modify, distribute, sell, lease, or reverse engineer any part of the Service except where permitted by law.</p>
        </section>

        <section className="legal-section">
          <h2>11) Subscriptions, billing, and cancellation</h2>
          <p>LiftPeak may offer paid auto-renewable subscriptions for access to paid app features, such as premium workout logging, progress tracking, and related tools described in the app at the time of purchase. A subscription is required only for features marked as paid or premium.</p>

          <h3>Subscription plans</h3>
          <p>LiftPeak currently offers these auto-renewable subscription options where available:</p>
          <ul>
            <li><strong>Yearly plan with a 14-day free trial:</strong> provides access for 1 year after the trial, then renews yearly unless canceled.</li>
            <li><strong>Discounted yearly plan:</strong> provides access for 1 year at the discounted price shown in the app, then renews yearly unless canceled.</li>
            <li><strong>Monthly plan:</strong> provides access for 1 month, then renews monthly unless canceled.</li>
          </ul>
          <p>Prices, currencies, taxes, trial eligibility, discounts, and renewal terms are shown in the app before purchase and may vary by country, platform, and offer eligibility. The price shown by Apple or Google at confirmation of purchase is the price that applies to that transaction.</p>

          <h3>Billing and auto-renewal</h3>
          <ul>
            <li>Purchases are processed through Apple&apos;s App Store or Google Play, depending on where you buy the subscription. RevenueCat helps us process subscription status and entitlements, but Apple or Google handles the payment transaction.</li>
            <li>Your payment method is charged when you confirm the purchase, unless a free trial or other introductory offer applies.</li>
            <li>Subscriptions automatically renew at the end of each billing period unless canceled before renewal. For App Store subscriptions, Apple generally requires cancellation at least 24 hours before the end of the current period to avoid the next charge, and Apple may charge for renewal within 24 hours before the period ends.</li>
            <li>If you start the yearly plan with a 14-day free trial, you will not be charged during the trial. Unless you cancel before the trial ends, the plan converts to a paid yearly subscription at the price shown before purchase. Any unused part of a free trial may be forfeited if you purchase another subscription or change plans, where applicable.</li>
            <li>If a renewal payment fails, Apple, Google, or RevenueCat may place the subscription in a grace period, account hold, retry, cancellation, or expiration state according to platform rules. Access to paid features may continue, pause, or end depending on that status.</li>
          </ul>

          <h3>Managing or canceling your subscription</h3>
          <p>You can manage or cancel your subscription through the platform account used for purchase. Deleting the LiftPeak app or your LiftPeak account does not automatically cancel an App Store or Google Play subscription.</p>
          <ul>
            <li><strong>Apple App Store:</strong> manage or cancel in your Apple ID subscription settings. Apple&apos;s cancellation help is available at <a href="https://support.apple.com/HT202039" target="_blank" rel="noreferrer">support.apple.com/HT202039</a>.</li>
            <li><strong>Google Play:</strong> manage or cancel in the Google Play subscriptions center at <a href="https://play.google.com/store/account/subscriptions" target="_blank" rel="noreferrer">play.google.com/store/account/subscriptions</a>.</li>
          </ul>
          <p>Cancellation stops future renewals. You usually keep access to paid features until the end of the current billing period or trial, unless the platform rules or applicable law provide otherwise.</p>

          <h3>Refunds and price changes</h3>
          <ul>
            <li>Refund requests for purchases made through Apple or Google are handled by that platform under its own policies and applicable law. LiftPeak cannot guarantee platform refunds.</li>
            <li>We may change subscription prices for valid reasons, such as changes to features, operating costs, taxes, platform fees, market conditions, or service improvements. We will provide advance notice where required. For Google Play opt-out price increases, we will provide at least 30 days&apos; prominent notice that includes the subscription name, current price, new price, effective date, and cancellation information. If platform rules require your consent to a price increase, your subscription may not continue at the new price unless you agree.</li>
          </ul>

          <h3>Platform terms</h3>
          <p>Apple&apos;s Media Services Terms and Google Play&apos;s terms also apply to purchases made through their platforms. If these Terms conflict with mandatory Apple or Google billing rules for your purchase, the applicable platform billing rules control for that purchase.</p>
        </section>

        <section className="legal-section">
          <h2>12) Disclaimer of warranties</h2>
          <p>The Service is provided <strong>"as is"</strong> and <strong>"as available."</strong> We do not guarantee that the Service will be uninterrupted, error-free, or always available.</p>
        </section>

        <section className="legal-section">
          <h2>13) Limitation of liability</h2>
          <p>To the maximum extent permitted by law:</p>
          <ul>
            <li>LiftPeak is not liable for indirect, incidental, special, consequential, or punitive damages, or loss of profits or data.</li>
            <li>LiftPeak's total liability for any claim related to the Service is limited to the amount you paid to LiftPeak in the <strong>last 12 months</strong> (or <strong>€0</strong> if you paid nothing), except where liability cannot be limited by law.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>14) Indemnity</h2>
          <p>If your use of the Service or your User Content causes claims or losses to others, you agree to defend and indemnify LiftPeak against related liabilities and expenses (including reasonable legal fees), to the extent permitted by law.</p>
        </section>

        <section className="legal-section">
          <h2>15) Termination</h2>
          <p>You can stop using the Service at any time and may delete your account (where available). We may suspend or terminate your access if:</p>
          <ul>
            <li>you violate these Terms,</li>
            <li>we must do so to comply with law,</li>
            <li>or it is necessary to protect users, the Service, or our rights.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>16) Changes to these Terms</h2>
          <p>We may update these Terms from time to time. If changes are material, we will provide notice in the app or by other reasonable means. Continued use after the effective date means you accept the updated Terms.</p>
        </section>

        <section className="legal-section">
          <h2>17) Governing law & jurisdiction</h2>
          <p>These Terms are governed by the laws of <strong>Estonia</strong>. Courts located in <strong>Estonia</strong> will have jurisdiction, except where mandatory consumer protection law provides otherwise.</p>
        </section>

        <section className="legal-section">
          <h2>18) Contact</h2>
          <p>Questions about these Terms:</p>
          <p><a href="mailto:contact@liftpeak.com">contact@liftpeak.com</a></p>
        </section>
      </main>

      <footer className="legal-footer">
        <Link to="/" className="back-link">Back to Home</Link>
      </footer>
    </div>
  );
}

export default Terms;



